/**
 * Risk Assessment Context
 * Provides global state for the floating AI Risk Assessment HUD
 * 
 * Features:
 * - Real-time risk calculation based on pursuit data
 * - Persistent expanded/minimized state via localStorage
 * - Alert hierarchy: Compliance (Red), Strategy (Yellow), Wins (Green)
 * - Integration with Ingestion Hub scanning status
 */

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

const RiskAssessmentContext = createContext(null);

// Storage key for HUD state persistence
const HUD_STATE_KEY = 'fedbd_risk_hud_state';

// Alert types for categorization
export const ALERT_TYPES = {
  COMPLIANCE: 'compliance',
  STRATEGY: 'strategy',
  DRAFTING: 'drafting'
};

// Alert severity levels
export const ALERT_SEVERITY = {
  CRITICAL: 'critical',   // Red - blocking issues
  WARNING: 'warning',     // Yellow - attention needed
  SUCCESS: 'success',     // Green - positive indicators
  INFO: 'info'           // Blue - informational
};

/**
 * Calculate risk alerts from pursuit data
 * Returns categorized alerts for the HUD display
 */
function calculateAlerts(pursuit, clientData = null) {
  const alerts = [];
  
  if (!pursuit) return alerts;
  
  // ========================================
  // DOCUMENT READINESS ALERTS
  // ========================================
  
  // Missing RFP documents (Required for Fit Score)
  if (!pursuit.rfp_document_count || pursuit.rfp_document_count === 0) {
    alerts.push({
      id: 'missing-rfp-docs',
      type: ALERT_TYPES.COMPLIANCE,
      severity: ALERT_SEVERITY.CRITICAL,
      title: 'No RFP Documents',
      message: 'Upload at least 1 RFP/Solicitation document to generate Fit Score',
      icon: 'FileText'
    });
  }
  
  // Missing Capability documents
  if (!pursuit.capability_document_count || pursuit.capability_document_count === 0) {
    if (!pursuit.total_kb_documents || pursuit.total_kb_documents === 0) {
      alerts.push({
        id: 'missing-capability-docs',
        type: ALERT_TYPES.STRATEGY,
        severity: ALERT_SEVERITY.WARNING,
        title: 'No Capability Documents',
        message: 'Upload at least 1 capability document to assess fit and produce the Fit Score',
        icon: 'Lightbulb'
      });
    }
  }
  
  // ========================================
  // COMPLIANCE ALERTS (Mandatory)
  // ========================================
  
  // Set-aside mismatch check
  if (pursuit.set_aside_type && pursuit.prime_metadata?.socioeconomic_status) {
    const setAside = pursuit.set_aside_type?.toLowerCase();
    const primeStatus = pursuit.prime_metadata.socioeconomic_status?.toLowerCase();
    
    // Critical: Set-aside requires specific certification
    const setAsideMap = {
      'sdvosb': ['sdvosb'],
      '8(a)': ['8a', '8(a)'],
      'wosb': ['wosb', 'edwosb'],
      'edwosb': ['edwosb'],
      'hubzone': ['hubzone'],
      'small business': ['sdvosb', 'vosb', '8a', '8(a)', 'wosb', 'edwosb', 'hubzone', 'sdb', 'small_business', 'small business']
    };
    
    const requiredCerts = setAsideMap[setAside] || [];
    const hasMatch = requiredCerts.some(cert => primeStatus.includes(cert));
    
    if (requiredCerts.length > 0 && !hasMatch && primeStatus !== 'none') {
      alerts.push({
        id: 'set-aside-mismatch',
        type: ALERT_TYPES.COMPLIANCE,
        severity: ALERT_SEVERITY.CRITICAL,
        title: 'Set-Aside Mismatch',
        message: `RFP requires ${pursuit.set_aside_type}, but Prime is ${pursuit.prime_metadata.socioeconomic_status}`,
        icon: 'ShieldAlert'
      });
    }
  }
  
  // Workshare validation
  const teamingPlan = pursuit.teaming_plan || [];
  const totalWorkshare = teamingPlan.reduce((sum, p) => sum + (parseInt(p.workshare) || 0), 0);
  
  if (totalWorkshare > 100) {
    alerts.push({
      id: 'workshare-exceeded',
      type: ALERT_TYPES.COMPLIANCE,
      severity: ALERT_SEVERITY.CRITICAL,
      title: 'Workshare Exceeded',
      message: `Total workshare is ${totalWorkshare}% (max 100%)`,
      icon: 'AlertTriangle'
    });
  }
  
  // Missing Prime Bidder
  if (!pursuit.prime_metadata?.entity_name) {
    alerts.push({
      id: 'missing-prime',
      type: ALERT_TYPES.COMPLIANCE,
      severity: ALERT_SEVERITY.WARNING,
      title: 'No Prime Bidder',
      message: 'Lead Prime Bidder not identified',
      icon: 'Crown'
    });
  }
  
  // Missing deadline
  if (!pursuit.response_deadline) {
    alerts.push({
      id: 'missing-deadline',
      type: ALERT_TYPES.COMPLIANCE,
      severity: ALERT_SEVERITY.WARNING,
      title: 'No Deadline Set',
      message: 'Response deadline not specified',
      icon: 'Calendar'
    });
  }
  
  // ========================================
  // STRATEGY ALERTS (Experience/Teaming)
  // ========================================
  
  // Agency experience bonus
  if (pursuit.experience_bonus > 0) {
    alerts.push({
      id: 'agency-experience',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'Agency Experience',
      message: pursuit.experience_explanation || `+${pursuit.experience_bonus} pts from past wins`,
      icon: 'Award'
    });
  }
  
  // Teaming partner alignment
  if (pursuit.teaming_score_bonus > 0) {
    const categories = pursuit.teaming_categories_covered?.join(', ') || '';
    alerts.push({
      id: 'teaming-alignment',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'Teaming Alignment',
      message: `+${pursuit.teaming_score_bonus} pts - Partners cover: ${categories}`,
      icon: 'Users'
    });
  }
  
  // No teaming partners warning
  if (teamingPlan.length === 0 && pursuit.set_aside_type?.toLowerCase() !== 'full and open') {
    alerts.push({
      id: 'no-teaming',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.WARNING,
      title: 'Solo Bid',
      message: 'No teaming partners identified - consider adding partners',
      icon: 'Users'
    });
  }
  
  // Full & Open competition advisory
  if (pursuit.set_aside_type?.toLowerCase() === 'full and open' && 
      pursuit.prime_metadata?.socioeconomic_status?.toLowerCase() === 'small_business') {
    alerts.push({
      id: 'full-open-small',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.INFO,
      title: 'High Competition',
      message: 'Full & Open + Small Business: Consider adding a Large Business partner',
      icon: 'Lightbulb'
    });
  }
  
  // NAICS compliance
  if (pursuit.naics_compliance_status === 'green') {
    alerts.push({
      id: 'naics-green',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'NAICS Match',
      message: `Primary NAICS ${pursuit.naics_code} registered`,
      icon: 'Check'
    });
  } else if (pursuit.naics_compliance_status === 'yellow') {
    alerts.push({
      id: 'naics-yellow',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.WARNING,
      title: 'NAICS Partial',
      message: `Secondary NAICS ${pursuit.naics_code} - verify capability`,
      icon: 'AlertCircle'
    });
  } else if (pursuit.naics_compliance_status === 'red') {
    alerts.push({
      id: 'naics-red',
      type: ALERT_TYPES.COMPLIANCE,
      severity: ALERT_SEVERITY.CRITICAL,
      title: 'NAICS Not Registered',
      message: `NAICS ${pursuit.naics_code} not in organization profile`,
      icon: 'XCircle'
    });
  }
  
  // PSC compliance
  if (pursuit.psc_compliance_status === 'green') {
    alerts.push({
      id: 'psc-green',
      type: ALERT_TYPES.STRATEGY,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'PSC Experience',
      message: `Direct past performance for PSC ${pursuit.psc_code}`,
      icon: 'Award'
    });
  }
  
  // ========================================
  // DRAFTING READINESS (Similarity/KB)
  // ========================================
  
  // Similarity matches
  if (pursuit.similarity_matches_count > 0) {
    alerts.push({
      id: 'similarity-matches',
      type: ALERT_TYPES.DRAFTING,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'Content Matches',
      message: `${pursuit.similarity_matches_count} reusable sections from Winning Proposals`,
      icon: 'FileText'
    });
  }
  
  // RFP processed status
  if (pursuit.rfp_shredded) {
    alerts.push({
      id: 'rfp-processed',
      type: ALERT_TYPES.DRAFTING,
      severity: ALERT_SEVERITY.SUCCESS,
      title: 'RFP Analyzed',
      message: 'Requirements extracted and indexed',
      icon: 'Brain'
    });
  }
  
  return alerts;
}

/**
 * Calculate Heuristic Fit Score from alerts and pursuit data
 * Uses the same calculation as Go/No-Go Dashboard for consistency
 */
function calculateHeuristicFitScore(pursuit, alerts) {
  if (!pursuit) return null;
  
  // Fit Score is ONLY available after AI Analysis has completed.
  // Before that, return null — the UI shows prerequisite/analysis-required states.
  
  // Check if AI analysis has been completed
  if (pursuit.ai_analysis_completed === true) {
    // Use competitive_alignment_score from AI analysis pipeline
    if (pursuit.competitive_alignment_score !== null && pursuit.competitive_alignment_score !== undefined) {
      return Math.max(0, Math.min(100, pursuit.competitive_alignment_score));
    }
  }
  
  // Also accept if competitive_alignment_score is set AND the pursuit has been through analysis
  // (for backward compat with pursuits analyzed before this change)
  if (pursuit.competitive_alignment_score !== null && 
      pursuit.competitive_alignment_score !== undefined && 
      pursuit.ai_analysis_completed_at) {
    return Math.max(0, Math.min(100, pursuit.competitive_alignment_score));
  }
  
  // No score available
  return null;
}

// calculateMetadataBaselineForHUD removed — metadata no longer contributes to Fit Score

export function RiskAssessmentProvider({ children }) {
  const { authToken } = useAuth();
  
  // Current pursuit being assessed
  const [currentPursuit, setCurrentPursuit] = useState(null);
  const [clientData, setClientData] = useState(null);
  
  // HUD UI state with localStorage persistence
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(HUD_STATE_KEY);
      return saved ? JSON.parse(saved).isExpanded : false;
    } catch {
      return false;
    }
  });
  
  // Visibility control - only show on specific routes
  const [isVisible, setIsVisible] = useState(false);
  
  // Scanning status for ingestion
  const [scanningStatus, setScanningStatus] = useState({});
  
  // AI Consultant Tip (async loaded)
  const [consultantTip, setConsultantTip] = useState(null);
  const [loadingTip, setLoadingTip] = useState(false);
  
  // Previous score for animation
  const [previousScore, setPreviousScore] = useState(0);
  
  // Persist expanded state
  useEffect(() => {
    try {
      localStorage.setItem(HUD_STATE_KEY, JSON.stringify({ isExpanded }));
    } catch {
      // Ignore storage errors
    }
  }, [isExpanded]);

  // Wipe all tenant-scoped state on auth clear (logout, session expiry).
  // AuthContext fires `fedbd:auth-cleared` so we don't have to import it
  // directly (avoids circular dependency and keeps the contract loose).
  useEffect(() => {
    const handler = () => {
      setCurrentPursuit(null);
      setClientData(null);
      setScanningStatus({});
      setConsultantTip(null);
      setLoadingTip(false);
      setPreviousScore(0);
      setIsVisible(false);
    };
    window.addEventListener('fedbd:auth-cleared', handler);
    return () => window.removeEventListener('fedbd:auth-cleared', handler);
  }, []);
  
  // Calculate alerts based on current pursuit
  const alerts = useMemo(() => {
    return calculateAlerts(currentPursuit, clientData);
  }, [currentPursuit, clientData]);
  
  // Calculate Heuristic Fit Score
  const heuristicFitScore = useMemo(() => {
    return calculateHeuristicFitScore(currentPursuit, alerts);
  }, [currentPursuit, alerts]);
  
  // Categorize alerts
  const categorizedAlerts = useMemo(() => {
    return {
      compliance: alerts.filter(a => a.type === ALERT_TYPES.COMPLIANCE),
      strategy: alerts.filter(a => a.type === ALERT_TYPES.STRATEGY),
      drafting: alerts.filter(a => a.type === ALERT_TYPES.DRAFTING)
    };
  }, [alerts]);
  
  // Update pursuit data
  const updatePursuit = useCallback((pursuitData) => {
    setPreviousScore(heuristicFitScore);
    setCurrentPursuit(pursuitData);
    setConsultantTip(null); // Clear tip when pursuit changes
  }, [heuristicFitScore]);
  
  // Update client data
  const updateClient = useCallback((client) => {
    setClientData(client);
  }, []);
  
  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  // Show/hide HUD
  const showHUD = useCallback(() => setIsVisible(true), []);
  const hideHUD = useCallback(() => setIsVisible(false), []);
  
  // Update scanning status for a document group
  const updateScanningStatus = useCallback((groupId, status) => {
    setScanningStatus(prev => ({
      ...prev,
      [groupId]: status
    }));
  }, []);
  
  // Clear scanning status
  const clearScanningStatus = useCallback(() => {
    setScanningStatus({});
  }, []);
  
  // Fetch AI consultant tip (async)
  const fetchConsultantTip = useCallback(async () => {
    if (!currentPursuit || !authToken || loadingTip) return;
    
    setLoadingTip(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/pursuits/${currentPursuit.id}/strategic-tip`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConsultantTip(data.tip);
      }
    } catch (error) {
      console.error('Failed to fetch consultant tip:', error);
    } finally {
      setLoadingTip(false);
    }
  }, [currentPursuit, authToken, loadingTip]);
  
  // Clear all state
  const clearRiskState = useCallback(() => {
    setCurrentPursuit(null);
    setClientData(null);
    setScanningStatus({});
    setConsultantTip(null);
    setIsVisible(false);
  }, []);
  
  const value = {
    // State
    currentPursuit,
    clientData,
    isExpanded,
    isVisible,
    heuristicFitScore,
    previousScore,
    alerts,
    categorizedAlerts,
    scanningStatus,
    consultantTip,
    loadingTip,
    
    // Actions
    updatePursuit,
    updateClient,
    toggleExpanded,
    showHUD,
    hideHUD,
    updateScanningStatus,
    clearScanningStatus,
    fetchConsultantTip,
    clearRiskState
  };
  
  return (
    <RiskAssessmentContext.Provider value={value}>
      {children}
    </RiskAssessmentContext.Provider>
  );
}

export function useRiskAssessment() {
  const context = useContext(RiskAssessmentContext);
  if (!context) {
    throw new Error('useRiskAssessment must be used within a RiskAssessmentProvider');
  }
  return context;
}

export default RiskAssessmentContext;
